import { ROCA } from '@somnhold/content';
import { cellId } from '../grid.ts';
import { worldToHex, type Hex } from '../hex.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { AllyRules, BedRules, ClimbRules, MouthsRules, RocaRules, WolfRules } from '../roca-types.ts';
import { ARCH_LINKS, BED_RADIUS, CLIMB_RADIUS, WOLF_RADIUS, type BedLayout, type ClimbLayout, type HallLayout, type WolfLayout } from './roca-layout.ts';
import { climbGoal, climbHold, climbOrder, climbResult, climbTick } from './climb.ts';
import { mouthsGoal, mouthsOff, mouthsOrder, mouthsResult, mouthsTick } from './mouths.ts';
import { bedGoal, bedResult, bedTick } from './ashbed.ts';
import { wolfGoal, wolfHold, wolfOff, wolfOrder, wolfResult, wolfTick } from './graywolf.ts';

/**
 * The Long Hunt's chapter 5 (docs/sendas/barbaro.md, «La guarida»): its rules from their layouts (the fight's builder
 * and the renderer's scenes build them the same way; lives in hit points worked out by the caller, seconds of D) and the
 * one door battle/senda.ts goes through for its four acts (`rules.senda.roca`).
 */

const ids = (R: number, hs: Hex[]): number[] =>
  hs
    .map((h) => cellId(R + 2, h.q, h.r))
    .filter((c) => c >= 0)
    .sort((a, b) => a - b);

const ally = (a: { every: number; reach: number }, hit: number): AllyRules => ({ every: a.every, hit, reach: a.reach });

/** The climb's rules: Ulla's blow `ullaHit` (hit points). */
export const climbRules = (L: ClimbLayout, wolfHp: number, crowHp: number, howlerHp: number, ullaHit: number): ClimbRules => {
  const C = ROCA.climb;
  return {
    way: L.way.map((p) => ({ ...p })),
    ledgeOf: L.ledgeOf.slice(),
    ledges: L.ledges.slice(),
    nests: L.nests.map((p) => ({ ...p })),
    mouth: { ...L.mouth },
    howlerAt: { ...L.howlerAt },
    ramps: L.ramps.map((r) => ids(CLIMB_RADIUS, r)),
    reach: C.reach,
    settle: C.settle,
    ledgeBand: C.ledge.slice(),
    pack: { first: C.pack.first, every: [C.pack.every[0], C.pack.every[1]], n: [C.pack.n[0], C.pack.n[1]] },
    crows: { ...C.crows },
    howler: { hp: howlerHp, at: C.howler.at, first: C.howler.first, every: C.howler.every, rear: C.howler.rear, howl: C.howler.howl, cutBp: C.howler.cutBp, raise: C.howler.raise, stun: C.howler.stun },
    wolfHp,
    crowHp,
    ulla: ally(ROCA.ulla, ullaHit),
    horn: { ...ROCA.horn },
  };
};

export const mouthsRules = (L: HallLayout, wolfHp: number, cubHp: number, ullaHit: number): MouthsRules => {
  const M = ROCA.mouths;
  return {
    mouths: L.mouths.map((p) => ({ ...p })),
    arches: L.arches.map((p) => ({ ...p })),
    links: ARCH_LINKS.map((l) => [l[0], l[1]] as [number, number]),
    cracks: L.puzzle.cracks.slice(),
    trace: L.puzzle.trace,
    best: L.puzzle.best,
    rim: L.rim.map((p) => ({ ...p })),
    reach: M.reach,
    strike: M.strike,
    breaks: M.breaks,
    need: M.need,
    settle: M.settle,
    cubs: { first: M.cubs.first, every: M.cubs.every, n: [M.cubs.n[0], M.cubs.n[1]] },
    wolves: { ...M.wolves },
    surge: { ...M.surge },
    cubHp,
    cubDmgBp: M.cubDmgBp,
    wolfHp,
    ulla: ally(ROCA.ulla, ullaHit),
    horn: { ...ROCA.horn },
  };
};

export const bedRules = (L: BedLayout, wolfHp: number, cubHp: number, ullaHit: number): BedRules => {
  const B = ROCA.bed;
  return {
    rooms: L.rooms.map((m) => ({ at: { ...m.at }, r: m.r, holes: m.holes.map((p) => ({ ...p })), gate: ids(BED_RADIUS, m.gate) })),
    bed: { ...L.bed },
    reach: B.reach,
    settle: B.settle,
    bands: B.bands.map((b) => [b[0], b[1]] as [number, number]),
    part: B.part,
    cubHp,
    cubDmgBp: B.cubDmgBp,
    wolfHp,
    ulla: ally(ROCA.ulla, ullaHit),
  };
};

/**
 * The fight's rules: which mouths act 2 left open (bits of `shut`), the one without tracks still standing (`bare`: its
 * index + 1, 0 none), Sigrun's axe taken or left, the path's enemy's scars.
 */
export const wolfRules = (L: WolfLayout, o: { hp: number; wolfHp: number; cubHp: number; ullaHit: number; ivarHit: number; shut: number; bare: number; axe: boolean; scars: number }): WolfRules => {
  const W = ROCA.wolf;
  const open = L.mouths.map((_, k) => ((o.shut >> k) & 1 ? 0 : 1));
  const trace = o.bare > 0 && open[o.bare - 1] ? o.bare - 1 : -1;
  return {
    bed: { ...L.bed },
    mouths: L.mouths.map((p) => ({ ...p })),
    open,
    trace,
    pillars: L.pillars.map((p) => ({ ...p })),
    slabs: L.slabs.map((x) => {
      const h = worldToHex(x.at.x, x.at.y);
      return { at: { ...x.at }, cell: cellId(WOLF_RADIUS + 2, h.q, h.r), mouth: x.mouth, cracks: x.cracks };
    }),
    crackFoot: { ...L.crackFoot },
    crackTop: { ...L.crackTop },
    summit: { ...L.summit },
    edges: L.edges.map((p) => ({ ...p })),
    island: L.island,
    stones: L.stones.map((p) => ({ ...p })),
    skiff: { ...L.skiff },
    denRim: L.denRim.map((p) => ({ ...p })),
    topRim: L.topRim.map((p) => ({ ...p })),
    ivarHurt: { ...L.ivarHurt },
    ivarUp: { ...L.ivarUp },
    start: { ...L.start },
    hp: o.hp,
    axe: o.axe,
    scars: o.scars,
    biteBp: W.biteBp,
    reload: W.reload,
    reach: W.reach,
    speed: W.speed,
    flee: W.flee,
    fleeBp: W.fleeBp,
    look: W.look,
    block: W.block,
    caught: W.caught,
    slowBp: W.slowBp,
    minSlowBp: W.minSlowBp,
    scarSlowBp: W.scarSlowBp,
    stun: W.stun,
    under: W.under,
    healBp: o.axe ? W.axeHealBp : W.healBp,
    rise: W.rise,
    diveBiteBp: W.diveBiteBp,
    phase2Bp: W.phase2Bp,
    phase3Bp: W.phase3Bp,
    phase3ShutBp: W.phase3ShutBp,
    huntBp: W.huntBp,
    howl: { ...W.howl, raise: [W.howl.raise[0], W.howl.raise[1]] },
    drop: { ...W.drop },
    leap: { ...W.leap },
    slab: { ...W.slab },
    boats: { ...W.boats },
    boat: { ...W.boat },
    cubs: { first: W.cubs.first, every: W.cubs.every, n: [W.cubs.n[0], W.cubs.n[1]] },
    cubHp: o.cubHp,
    cubDmgBp: W.cubDmgBp,
    wolfHp: o.wolfHp,
    ulla: ally(ROCA.ulla, o.ullaHit),
    ivar: { ...ally(ROCA.ivar, o.ivarHit), speed: ROCA.ivar.speed, follow: ROCA.ivar.follow, coverBp: ROCA.ivar.coverBp },
    horn: { ...ROCA.horn },
  };
};

const rr = (s: BattleState): RocaRules | undefined => s.rules?.senda?.roca;

export const rocaTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = rr(s);
  if (r?.climb) climbTick(s, cue);
  else if (r?.mouths) mouthsTick(s, cue);
  else if (r?.bed) bedTick(s, cue);
  else if (r?.wolf) wolfTick(s, cue);
};

/** The hero's tap on one of the act's marks (an arch, a pillar, a stone, the horn). */
export const rocaOrder = (s: BattleState, h: Hero, at: number, cue: (c: string) => void): void => {
  const r = rr(s);
  if (r?.climb) climbOrder(s, at, cue);
  else if (r?.mouths) mouthsOrder(s, h, at, cue);
  else if (r?.wolf) wolfOrder(s, h, at, cue);
};

/** Another order of the hero's own: what it was sent to strike is off. */
export const rocaMarkOff = (s: BattleState): void => {
  const r = rr(s);
  if (r?.mouths) mouthsOff(s);
  else if (r?.wolf) wolfOff(s);
};

export const rocaGoal = (s: BattleState): { done: number; need: number } => {
  const r = rr(s);
  return r?.climb ? climbGoal(s) : r?.mouths ? mouthsGoal(s) : r?.bed ? bedGoal(s) : wolfGoal(s);
};

export const rocaResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = rr(s);
  return r?.climb ? climbResult(s) : r?.mouths ? mouthsResult(s) : r?.bed ? bedResult(s) : r?.wolf ? wolfResult(s) : null;
};

/** The act moved this nightmare itself (true). */
export const rocaHold = (s: BattleState, e: Enemy): boolean => {
  const r = rr(s);
  if (r?.climb) return climbHold(s, e);
  if (r?.wolf) return wolfHold(s, e);
  return false;
};
